import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHashtag, faPen, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import protocoloService from '../../services/protocoloService';

/**
 * Acerto do próximo número de protocolo com o livro de papel.
 *
 * O livro continua recebendo entrada enquanto o sistema não entra no ar, então
 * o número certo só se conhece no dia da virada. Sem esta tela, ajustar exigiria
 * um UPDATE no MySQL — virada dependendo de quem tem acesso ao banco.
 *
 * Aparece só para quem tem `protocolos.sequencia`.
 */
const SequenciaProtocoloPanel = () => {
  const [sequencia, setSequencia] = useState(null);
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const resposta = await protocoloService.lerSequencia();
      setSequencia(resposta.data);
    } catch (err) {
      // Sem permissão o endpoint responde 403 e o painel simplesmente não
      // aparece — não é erro que mereça alarmar a tela toda.
      if (err.status !== 403) console.error('Erro ao ler a sequência de protocolos:', err);
      setSequencia(null);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  if (!sequencia) return null;

  const abrirEdicao = () => {
    setValor(String(sequencia.proximoNumero));
    setErro('');
    setEditando(true);
  };

  const salvar = async (e) => {
    e.preventDefault();
    if (salvando) return;

    setSalvando(true);
    setErro('');

    try {
      const resposta = await protocoloService.ajustarSequencia(Number(valor));
      toast.success(resposta.message || 'Sequência ajustada.');
      setEditando(false);
      await carregar();
    } catch (err) {
      setErro(err.fieldErrors?.proximoNumero?.[0] || err.message || 'Não foi possível ajustar.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="mb-4 w-full max-w-full rounded border border-gray-200 bg-gray-50 p-3 sm:p-4">
      {!editando ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2 text-gray-700 min-w-0">
            <FontAwesomeIcon icon={faHashtag} className="text-gray-500 shrink-0" />
            <span className="text-sm">
              Próximo protocolo: <strong>{sequencia.proximoNumero}</strong>
              {sequencia.ultimoUsado > 0 && (
                <span className="text-gray-500"> · último emitido: {sequencia.ultimoUsado}</span>
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={abrirEdicao}
            className="self-start sm:self-auto min-h-11 px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPen} />
            <span>Ajustar numeração</span>
          </button>
        </div>
      ) : (
        <form onSubmit={salvar}>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="proximoNumero" className="text-sm font-medium text-gray-700">
              Próximo número de protocolo
            </label>
            <button
              type="button"
              onClick={() => setEditando(false)}
              className="p-1 rounded text-gray-500 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
              aria-label="Cancelar ajuste"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <p className="mb-2 text-sm text-gray-600">
            Use o número seguinte ao último escrito no livro de papel. Precisa ser{' '}
            <strong>{sequencia.minimoPermitido}</strong> ou maior — abaixo disso o número já foi
            emitido. Protocolo entregue ao requerente não pode ser renumerado.
          </p>

          <div className="flex flex-col sm:flex-row gap-2">
            <input
              id="proximoNumero"
              type="number"
              inputMode="numeric"
              min={sequencia.minimoPermitido}
              value={valor}
              onChange={(e) => { setValor(e.target.value); setErro(''); }}
              className={`w-full sm:w-48 min-h-11 px-3 py-2 rounded border focus:outline-none focus:ring-2 ${
                erro ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-green-400'
              }`}
            />
            <button
              type="submit"
              disabled={salvando}
              className="min-h-11 px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {salvando && <FontAwesomeIcon icon={faSpinner} spin />}
              <span>{salvando ? 'Salvando...' : 'Salvar'}</span>
            </button>
          </div>

          {erro && <p className="mt-1 text-sm text-red-600">{erro}</p>}
        </form>
      )}
    </div>
  );
};

export default SequenciaProtocoloPanel;
